import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { getDevProfiles, inviteDevTeam, DevProfile } from '../../modules/platform/api/devteam';
import { formatDateTime, formatRole } from '../../utils/format';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { validateEmail } from '../../utils/validation';

interface InviteFormData {
  email: string;
  invitee_name: string;
  access_level: string;
  notes?: string;
}

const DEFAULT_ACCESS_LEVEL = 'ops';

export const InviteDevTeam: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [profiles, setProfiles] = useState<DevProfile[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>({
    defaultValues: {
      access_level: DEFAULT_ACCESS_LEVEL,
      email: '',
      invitee_name: '',
      notes: '',
    },
  });

  const profileColumns = useMemo(
    () => [
      { label: 'Name', key: 'name' },
      { label: 'Email', key: 'email' },
      { label: 'Access Level', key: 'access_level' },
      { label: 'Status', key: 'status' },
      { label: 'Joined', key: 'created_at' },
    ],
    []
  );

  const fetchProfiles = useCallback(async () => {
    setIsLoadingProfiles(true);
    try {
      const rows = await getDevProfiles({ limit: 50, skip: 0 });
      setProfiles(rows);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoadingProfiles(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const onSubmit = async (data: InviteFormData) => {
    setIsLoading(true);
    try {
      await inviteDevTeam({
        email: data.email,
        invitee_name: data.invitee_name,
        access_level: data.access_level,
        notes: data.notes,
      });
      showSuccessToast('Invite sent successfully!');
      reset({
        email: '',
        invitee_name: '',
        access_level: DEFAULT_ACCESS_LEVEL,
        notes: '',
      });
      setIsModalOpen(false);
      await fetchProfiles();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Invite Dev Team Member"
        subtitle="Send an invitation to another developer and review the list of current profiles"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setIsModalOpen(true)}>Invite Dev Team</Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchProfiles}
              isLoading={isLoadingProfiles}
            >
              Refresh list
            </Button>
          </div>
        }
      />

      <Card className="flex flex-col">
        <CardHeader className="mb-0">
          <CardTitle>Dev Profiles</CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing the latest {profiles.length} profiles available to the Dev team
          </p>
        </CardHeader>
        <CardContent padding="none" className="px-0">
          {isLoadingProfiles ? (
            <div className="flex h-48 items-center justify-center">
              <Spinner />
            </div>
          ) : profiles.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              No dev profiles found. Invite someone to populate this list.
            </div>
          ) : (
            <Table className="min-w-full">
              <TableHeader>
                <tr>
                  {profileColumns.map((column) => (
                    <TableHead key={column.key}>{column.label}</TableHead>
                  ))}
                </tr>
              </TableHeader>
              <TableBody>
                {profiles.map((profile, index) => (
                  <TableRow key={profile._id ?? profile.id ?? profile.email ?? index}>
                    <TableCell className="font-medium text-gray-900 dark:text-white">
                      {profile.name || '—'}
                    </TableCell>
                    <TableCell>{profile.email || '—'}</TableCell>
                    <TableCell>{formatRole(profile.access_level || DEFAULT_ACCESS_LEVEL)}</TableCell>
                    <TableCell>{profile.status ?? '—'}</TableCell>
                    <TableCell>
                      {profile.created_at ? formatDateTime(profile.created_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Invite Dev Team Member"
        size="lg"
      >
        <Card className="space-y-4">
          <CardHeader>
            <CardTitle>Invite Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="Email Address"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  validate: (value) => validateEmail(value) || 'Invalid email address',
                })}
                error={errors.email?.message}
                placeholder="dev@example.com"
                helperText="An invite will be sent to this email address"
              />

              <Input
                label="Invitee Name"
                {...register('invitee_name', {
                  required: 'Name is required',
                  minLength: { value: 3, message: 'Please provide at least 3 characters' },
                })}
                error={errors.invitee_name?.message}
                placeholder="Another Dev Kumar"
              />

              <Select
                label="Access Level"
                options={[
                  { label: 'Operations (ops)', value: 'ops' },
                  { label: 'Engineering (platform)', value: 'platform' },
                  { label: 'Admin', value: 'admin' },
                ]}
                {...register('access_level', {
                  required: 'Access level is required',
                })}
                error={errors.access_level?.message}
              />

              <Textarea
                label="Notes"
                {...register('notes')}
                placeholder="Optional context for this invite"
              />

              <Button type="submit" isLoading={isLoading} className="w-full">
                Send Invite
              </Button>
            </form>
          </CardContent>
        </Card>
      </Modal>
    </Layout>
  );
};


